import { Link as RouterLink } from 'react-router-dom';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { isMetricDeltaVisible, isMetricVisible } from '@/features/shared/metricVisibility';
import { resolveEntityTags } from '@/features/shared/tags';
import { Box, formatStatusLabel, getStatusTextColor, Link, Text } from '@/ui';
import { getStringFilterOperators } from '@/ui/composites/grid/gridOperators';
import { NameWithRunCell, RelativeTimeTooltipCell, TagsInlineCell } from '@/ui/composites/table';
import { buildMetricColumns, type MetricColumnKey } from '@/ui/composites/table/tableUtils';
import type { GridColDef, GridRenderCellParams } from '@/ui/primitives';
import type { BuildStatus, MetricThresholdGroup, PipelineListItemDto } from '@flowqa/shared';

type PipelinesColumnsOptions = {
  selectedProjectId: string | null;
  thresholds: MetricThresholdGroup;
  resolveThresholds: (row: { projectId: string }) => MetricThresholdGroup | undefined;
  onStatusSelect?: (status: BuildStatus | 'none') => void;
  showDeltas?: boolean;
};

const columnFlex = {
  name: 1,
  latest: 0.5,
  lastFinished: 0.5,
  passRate: 0.5,
  frequency: 0.5,
  avgDuration: 0.5,
  flakeScore: 0.5,
  mttf: 0.5,
  delta: 0.4,
  tags: 0.75,
};

export const buildPipelinesColumns = ({
  selectedProjectId,
  thresholds,
  resolveThresholds,
  onStatusSelect,
  showDeltas = true,
}: PipelinesColumnsOptions): Array<GridColDef<PipelineListItemDto>> => {
  const stringOperators = getStringFilterOperators();
  const deltasEnabled = showDeltas;
  const resolvedThresholds = (row: PipelineListItemDto): MetricThresholdGroup => {
    if (selectedProjectId) return thresholds;
    return resolveThresholds(row) ?? thresholds;
  };
  const metricKeys: MetricColumnKey[] = [
    'passRate',
    'frequency',
    'avgDuration',
    ...(isMetricVisible('flakeScore') ? (['flakeScore'] as const) : []),
    ...(isMetricVisible('mttf') ? (['mttf'] as const) : []),
  ];

  const nameColumn: GridColDef<PipelineListItemDto> = {
    field: 'name',
    headerName: 'Pipeline',
    flex: columnFlex.name,
    filterOperators: stringOperators,
    renderCell: (params) => (
      <NameWithRunCell
        primary={{ label: params.row.name, href: `/pipelines/${resolvePipelineSlug(params.row)}` }}
      />
    ),
  };

  const latestStatusColumn: GridColDef<PipelineListItemDto> = {
    field: 'latestBuildStatus',
    headerName: 'Latest Status',
    flex: columnFlex.latest,
    filterable: false,
    sortable: false,
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => {
      const latestBuild = params.row.latestBuild ?? null;
      const status = latestBuild?.status ?? null;
      const isClickable = Boolean(status && onStatusSelect);
      const label = status ? formatStatusLabel(status) : 'No Builds';
      const runNumber = resolveBuildRunNumber(latestBuild?.buildKey ?? null);
      const pipelineSlug = resolvePipelineSlug(params.row);
      const buildHref =
        status && runNumber !== null ? `/pipelines/${pipelineSlug}/runs/${runNumber}` : null;
      const labelNode = buildHref ? (
        <Link
          component={RouterLink}
          to={buildHref}
          underline="hover"
          color="inherit"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <Text role="body" component="span" color="inherit">
            {label}
          </Text>
        </Link>
      ) : (
        label
      );
      if (isClickable && !buildHref) {
        return (
          <Box
            component="span"
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              onStatusSelect?.(status ?? 'none');
            }}
          >
            <Text
              role="body"
              component="span"
              sx={{
                color: (theme) => getStatusTextColor(theme, status),
              }}
              transform="uppercase"
            >
              {labelNode}
            </Text>
          </Box>
        );
      }
      return (
        <Text
          role="body"
          component="span"
          sx={{
            color: (theme) => getStatusTextColor(theme, status),
          }}
          transform="uppercase"
        >
          {labelNode}
        </Text>
      );
    },
  };

  const lastFinishedColumn: GridColDef<PipelineListItemDto, Date | string | null> = {
    field: 'latestBuildTimestamp',
    headerName: 'Last Finished',
    flex: columnFlex.lastFinished,
    filterable: false,
    sortable: true,
    align: 'right',
    headerAlign: 'right',
    valueGetter: (_value, row) => row.latestBuild?.timestamp ?? null,
    renderCell: (params: GridRenderCellParams<PipelineListItemDto, Date | string | null>) => (
      <RelativeTimeTooltipCell value={params.value} />
    ),
  };

  const metricColumns = buildMetricColumns<PipelineListItemDto>({
    enabledKeys: metricKeys,
    includeDeltas: (key) => {
      if (!deltasEnabled) return false;
      if (key === 'flakeScore') return isMetricDeltaVisible('flakeScore');
      if (key === 'mttf') return isMetricDeltaVisible('mttf');
      return true;
    },
    fields: {
      passRate: 'metrics.passRate',
      frequency: 'metrics.frequencyPerDay',
      avgDuration: 'metrics.averageDurationMs',
      flakeScore: 'metrics.flakeScore',
      mttf: 'metrics.mttfSeconds',
    },
    deltaFields: {
      passRate: 'metrics.delta.passRate',
      frequency: 'metrics.delta.frequencyPerDay',
      avgDuration: 'metrics.delta.averageDurationMs',
      flakeScore: 'metrics.delta.flakeScore',
      mttf: 'metrics.delta.mttfSeconds',
    },
    flex: {
      passRate: columnFlex.passRate,
      frequency: columnFlex.frequency,
      avgDuration: columnFlex.avgDuration,
      flakeScore: columnFlex.flakeScore,
      mttf: columnFlex.mttf,
    },
    deltaFlex: {
      passRate: columnFlex.delta,
      frequency: columnFlex.delta,
      avgDuration: columnFlex.delta,
      flakeScore: columnFlex.delta,
      mttf: columnFlex.delta,
    },
    thresholds: thresholds,
    resolveThresholds: (row) => resolvedThresholds(row),
    align: 'right',
    headerAlign: 'right',
  });

  const tagsColumn: GridColDef<PipelineListItemDto> = {
    field: 'tags',
    headerName: 'Tags',
    flex: columnFlex.tags,
    filterable: false,
    sortable: false,
    renderCell: (params) => <TagsInlineCell tags={resolveEntityTags(params.row)} />,
  };

  return [nameColumn, latestStatusColumn, lastFinishedColumn, ...metricColumns, tagsColumn];
};
